type LinkedProductRef = {
  kind: 'publicCode' | 'slug'
  value: string
}

type MetadataRow = { key: string; value: string }
type CustomRow = { field: { key: string; label: string }; value: unknown }

const PRODUCT_PUBLIC_CODE_REGEX = /^[A-HJ-NP-Z2-9]{8}$/i
const RELATED_FIELD_KEYWORDS = [
  'accessor',
  'compatib',
  'correlat',
  'abbinat',
  'complement',
  'bundle',
  'upsell',
  'cross-sell',
  'cross_sell',
  'linked-product',
  'related-product',
]

const hasRelatedKeyword = (value: string) =>
  RELATED_FIELD_KEYWORDS.some((keyword) => value.toLowerCase().includes(keyword))

const normalizeLinkedRefToken = (token: string): LinkedProductRef | null => {
  const trimmed = token.trim()
  if (!trimmed) return null

  const lower = trimmed.toLowerCase()
  if (lower.startsWith('code:')) {
    const code = trimmed.slice('code:'.length).trim().toUpperCase()
    return PRODUCT_PUBLIC_CODE_REGEX.test(code) ? { kind: 'publicCode', value: code } : null
  }
  if (lower.startsWith('slug:')) {
    const slug = trimmed.slice('slug:'.length).trim().toLowerCase()
    return slug ? { kind: 'slug', value: slug } : null
  }

  const directCode = trimmed.toUpperCase()
  if (PRODUCT_PUBLIC_CODE_REGEX.test(directCode)) {
    return { kind: 'publicCode', value: directCode }
  }

  const pathname = (() => {
    try {
      return new URL(trimmed, 'https://shop.local').pathname
    } catch {
      return ''
    }
  })()

  if (!pathname) return null

  const dpMatch = pathname.match(/\/dp\/([^/?#]+)/i)
  if (dpMatch?.[1]) {
    const code = decodeURIComponent(dpMatch[1]).trim().toUpperCase()
    if (PRODUCT_PUBLIC_CODE_REGEX.test(code)) {
      return { kind: 'publicCode', value: code }
    }
  }

  const productsMatch = pathname.match(/\/products\/(.+)$/i)
  if (!productsMatch?.[1]) return null

  const segments = productsMatch[1]
    .split('/')
    .map((segment) => decodeURIComponent(segment).trim())
    .filter(Boolean)
  const slug = segments[segments.length - 1]
  return slug ? { kind: 'slug', value: slug.toLowerCase() } : null
}

export const extractLinkedProductRefs = ({
  metadataRows,
  customRows,
}: {
  metadataRows: MetadataRow[]
  customRows: CustomRow[]
}) => {
  const refs: LinkedProductRef[] = []
  const pushRefsFromText = (rawValue: string) => {
    rawValue
      .split(/[,\n;|]+/)
      .map((entry) => normalizeLinkedRefToken(entry))
      .forEach((entry) => {
        if (entry) refs.push(entry)
      })
  }

  metadataRows.forEach((entry) => {
    if (!hasRelatedKeyword(entry.key)) return
    pushRefsFromText(entry.value)
  })

  customRows.forEach((row) => {
    const source = `${row.field.key} ${row.field.label}`
    if (!hasRelatedKeyword(source)) return
    if (Array.isArray(row.value)) {
      row.value.filter((entry): entry is string => typeof entry === 'string').forEach(pushRefsFromText)
      return
    }
    if (typeof row.value === 'string') {
      pushRefsFromText(row.value)
    }
  })

  const deduped = new Map<string, LinkedProductRef>()
  refs.forEach((ref) => deduped.set(`${ref.kind}:${ref.value}`, ref))
  return Array.from(deduped.values()).slice(0, 8)
}

export type { LinkedProductRef }
